<template>
	<VerticalLayout>
		<FormButtonsItem :span="24">
			<PopconfirmButton
				type="primary"
				:title="$t('settings.restartForm.restartConfirmation.title')"
				:ok-text="$t('settings.restartForm.restartConfirmation.confirm')"
				:cancel-text="$t('settings.restartForm.restartConfirmation.cancel')"
				:onConfirm="onRestartButtonClick"
			>
				{{ $t('settings.restartForm.restartContainerButtonLabel') }}
			</PopconfirmButton>
			<PopconfirmButton
				type="primary"
				:title="$t('settings.restartForm.stopConfirmation.title')"
				:ok-text="$t('settings.restartForm.stopConfirmation.confirm')"
				:cancel-text="$t('settings.restartForm.stopConfirmation.cancel')"
				:onConfirm="onStopButtonClick"
			>
				{{ $t('settings.restartForm.stopContainerButtonLabel') }}
			</PopconfirmButton>
		</FormButtonsItem>
		<FormButtonsItem :span="24">
			<TaskRunButton
				buttonType="default"
				taskLabel="run_script_restart_caster"
				buttonLabel="settings.restartForm.restartCasterButtonLabel"
				useConfirmation
				confirmationTitleLabel="settings.restartForm.restartCasterConfirmationTitle"
			/>
			<TaskRunButton
				buttonType="default"
				taskLabel="run_script_restart_near"
				buttonLabel="settings.restartForm.restartNearButtonLabel"
				useConfirmation
				confirmationTitleLabel="settings.restartForm.restartNearConfirmationTitle"
			/>
		</FormButtonsItem>
		<FormButtonsItem :span="24">
			<TaskRunButton
				buttonType="default"
				taskLabel="run_script_restart_uwsgi"
				:taskInputData="{ logging: true }"
				buttonLabel="settings.restartForm.restartWebWithLogsButtonLabel"
				useConfirmation
				waitLonger
				confirmationTitleLabel="settings.restartForm.restartWebWithLogsConfirmationTitle"
			/>
			<TaskRunButton
				buttonType="default"
				taskLabel="run_script_restart_uwsgi"
				:taskInputData="{ logging: false }"
				buttonLabel="settings.restartForm.restartWebWithoutLogsButtonLabel"
				useConfirmation
				waitLonger
				confirmationTitleLabel="settings.restartForm.restartWebWithoutLogsConfirmationTitle"
			/>
		</FormButtonsItem>
	</VerticalLayout>
	<Drawer
		v-model:visible="isDrawerVisible"
		:closable="false"
		:maskClosable="false"
		:keyboard="false"
		placement="top"
		height="75vh"
	>
		<div class="restarting-drawer-wrapper">
			<Result :title="drawerTitle" :sub-title="drawerSubTitle" :status="drawerStatus">
				<template #extra>
					<Button v-if="isDrawerButtonVisible" type="primary" @click="onClose">{{ $t('settings.restartForm.restartingDrawerCloseButtonLabel') }}</Button>
				</template>
			</Result>
		</div>
	</Drawer>
</template>

<script>
import { ref, computed } from 'vue';
import { Button, Drawer, Result } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';

import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import TaskRunButton from '@/components/common/TaskRunButton.vue';
import FormButtonsItem from '@/components/common/FormButtonsItem.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';

import { useBaseStationsStore } from '@/stores/baseStations';
import { showErrorMessage } from '@/components/common/errorMessage';

export default {
	components: {
		Button, Drawer, Result,
		PopconfirmButton, TaskRunButton, FormButtonsItem,
		VerticalLayout,
	},
	setup() {
		const { t } = useI18n();
		const drawerStatus = ref('info');
		const isDrawerVisible = ref(false);
		const isDrawerButtonVisible = ref(false);
		const isRestartingTimer = ref(0);
		const restartContainerTaskId = ref(0);
		const restartContainerSuccessCount = ref(0);
		const drawerPrefix = ref('restarting');
		const drawerTitle = computed(() => t('settings.restartForm.' + drawerPrefix.value + '.title.' + drawerStatus.value));
		const drawerSubTitle = computed(() => {
			if (drawerStatus.value === 'success') {
				return '';
			}
			return t('settings.restartForm.' + drawerPrefix.value + '.subTitle.' + drawerStatus.value);
		});
		const showResult = (success) => {
			isDrawerButtonVisible.value = true;
			drawerStatus.value = success ? 'success' : 'warning';
			clearInterval(isRestartingTimer.value);
			useBaseStationsStore().setAutoUpdates(true);
		};
		const onRestartButtonClick = () => {
			drawerPrefix.value = 'restarting';
			isDrawerButtonVisible.value = false;
			Api.containerPower('restart', response => {
				if (response.status === 'ok') {
					restartContainerSuccessCount.value = 0;
					drawerStatus.value = 'info';
					isDrawerVisible.value = true;
					restartContainerTaskId.value = response['task_id']
					useBaseStationsStore().setAutoUpdates(false);
					isRestartingTimer.value = setInterval(() => {
						Api.containerCheckStatus(restartContainerTaskId.value, response => {
							if (response.status === 'failed') {
								showResult(false);
							} else if (response.status === 'ok') {
								restartContainerSuccessCount.value++;
								if (restartContainerSuccessCount.value >= 4) {
									showResult(true);
								}
							}
						});
					}, 10000);
				} else {
					showErrorMessage(response.errors);
				}
			});
		};
		const onStopButtonClick = () => {
			drawerPrefix.value = 'stopping';
			isDrawerButtonVisible.value = false;
			Api.containerPower('shutdown', response => {
				if (response.status === 'ok') {
					drawerStatus.value = 'info';
					isDrawerVisible.value = true;
					useBaseStationsStore().setAutoUpdates(false);
				} else {
					showErrorMessage(response.errors);
				}
			});
		};
		const onClose = () => {
			isDrawerVisible.value = false;
		};
		
		return {
			drawerStatus,
			isDrawerVisible,
			isDrawerButtonVisible,
			drawerTitle,
			drawerSubTitle,
			onStopButtonClick,
			onRestartButtonClick,
			onClose,
		};
	}
}
</script>

<style scoped>
.restarting-drawer-wrapper {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
}
</style>
